/**
 * Consent-gated loader for the TikTok Pixel (`window.ttq`).
 *
 * The TikTok pixel sets advertising cookies (`_tt_enable_cookie`, `_ttp`,
 * `ttcsid`) and sends hits to analytics.tiktok.com — non-essential tracking
 * that Quebec Law 25 / PIPEDA require opt-in for BEFORE it runs. The base
 * snippet used to load eagerly from index.html and set its cookies on the very
 * first paint, before any consent decision (found live 2026-08-01) — the exact
 * gap the Meta Pixel had (QA F11) before it was routed through this same gate.
 *
 * Nothing about TikTok touches the visitor until `initTikTokPixel()` runs, and
 * that only happens after the visitor accepts in CookieConsent (src/main.tsx).
 * Default state = pixel dark. Mirrors initMetaPixel() / initPostHog().
 */

import { hasAnalyticsConsent } from "@/lib/consent";

// Was hard-coded in the index.html snippet; this is now the single place the
// pixel is configured.
const PIXEL_ID = "D9CE5HRC77UBS5FSDVTG";

interface Ttq {
  methods: string[];
  setAndDefer: (target: unknown, method: string) => void;
  instance: (id: string) => unknown;
  load: (id: string, options?: Record<string, unknown>) => void;
  page: () => void;
  push: (...args: unknown[]) => void;
  [key: string]: unknown;
}

declare global {
  interface Window {
    ttq?: Ttq;
    TiktokAnalyticsObject?: string;
  }
}

// Guards a double init (idle callback + consent event in the same session).
let initialized = false;

/**
 * Load the TikTok pixel and fire the initial page() hit.
 *
 * No-ops unless the visitor has accepted analytics consent, so it is safe to
 * call eagerly — the consent check is the gate.
 */
export function initTikTokPixel(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (initialized || window.ttq) return;
  initialized = true;

  // Standard TikTok base snippet, ported from the old inline index.html script.
  // Builds the ttq command queue, injects events.js, then load + page. Because
  // this runs post-consent, no TikTok cookie or hit precedes opt-in.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (w: any, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq: any = (w[t] = w[t] || []);
    ttq.methods = [
      "page", "track", "identify", "instances", "debug", "on", "off", "once",
      "ready", "alias", "group", "enableCookie", "disableCookie",
      "holdConsent", "revokeConsent", "grantConsent",
    ];
    ttq.setAndDefer = function (obj: any, method: string) {
      obj[method] = function (...args: unknown[]) {
        obj.push([method, ...args]);
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (id: string) {
      const inst = ttq._i[id] || [];
      for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(inst, ttq.methods[n]);
      return inst;
    };
    ttq.load = function (id: string, options?: Record<string, unknown>) {
      const src = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
      ttq._i[id]._u = src;
      ttq._t = ttq._t || {};
      ttq._t[id] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[id] = options || {};
      const script = d.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = `${src}?sdkid=${id}&lib=${t}`;
      const first = d.getElementsByTagName("script")[0];
      if (first && first.parentNode) {
        first.parentNode.insertBefore(script, first);
      } else {
        (d.head ?? d.body ?? d.documentElement).appendChild(script);
      }
    };
    ttq.load(PIXEL_ID);
    ttq.page();
  })(window, document, "ttq");
  /* eslint-enable @typescript-eslint/no-explicit-any */
}
